if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
        });
      }

      hide(preventFocus = false) {
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        this.releaseCampaignFocus();
        this.modalContent.innerHTML = '';

        if (preventFocus) this.openedBy = null;
        super.hide();
      }

      show(opener) {
        if (this.campaignLoading || this.hasAttribute('open')) return;
        this.campaignLoading = true;
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        opener.querySelector('.loading__spinner').classList.remove('hidden');

        fetch(opener.getAttribute('data-product-url'))
          .then((response) => response.text())
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            const bootsNative = responseHTML.querySelector('template[data-boots-native-product]');
            if (bootsNative) { responseHTML.querySelector('tcs-boots-purchase')?.remove(); responseHTML.body.prepend(bootsNative.content.cloneNode(true)); bootsNative.remove(); }
            const productElement = responseHTML.querySelector('product-info');

            this.preprocessHTML(productElement);
            HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }
            if (window.ProductModel) window.ProductModel.loadShopifyXR();

            this.modalContent.querySelector('product-component')?.dispatchViewEvent?.();

            super.show(opener);
            if (this.hasAttribute('data-campaign-dialog')) this.containCampaignFocus();
          })
          .finally(() => {
            this.campaignLoading = false;
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            opener.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      campaignTabStops() {
        const visible = (element) => element.checkVisibility
          ? element.checkVisibility({ visibilityProperty: true, opacityProperty: true })
          : element.getClientRects().length > 0 && getComputedStyle(element).visibility !== 'hidden';
        return Array.from(this.querySelectorAll(
          'a[href], button, input, select, textarea, summary, [tabindex], iframe'
        )).filter((element) => {
          if (element.tabIndex < 0 || element.disabled || element.closest('[inert], [hidden]')) return false;
          if (!visible(element)) return false;
          if (element.matches('input[type="radio"]') && element.name) {
            const group = Array.from(this.querySelectorAll('input[type="radio"]'))
              .filter((radio) => radio.name === element.name && radio.form === element.form && !radio.disabled && visible(radio) && !radio.closest('[inert], [hidden]'));
            return element === (group.find((radio) => radio.checked) || group[0]);
          }
          return true;
        });
      }

      containCampaignFocus() {
        this.releaseCampaignFocus();
        removeTrapFocus();
        this.campaignBackground = Array.from(document.body.children)
          .filter((element) => element !== this && !element.inert);
        this.campaignBackground.forEach((element) => { element.inert = true; });
        this.campaignKeydown = (event) => {
          if (!this.hasAttribute('open')) return;
          if (event.key !== 'Tab') return;
          const stops = this.campaignTabStops();
          const first = stops[0] || this.querySelector('[role="dialog"]');
          const last = stops[stops.length - 1] || first;
          const active = document.activeElement;
          if (!stops.length || !this.contains(active) ||
              (event.shiftKey && (active === first || !stops.includes(active))) ||
              (!event.shiftKey && (active === last || !stops.includes(active)))) {
            event.preventDefault();
            (event.shiftKey ? last : first).focus();
          }
        };
        this.campaignFocusin = (event) => {
          if (this.hasAttribute('open') && !this.contains(event.target)) {
            (this.campaignTabStops()[0] || this.querySelector('[role="dialog"]')).focus();
          }
        };
        document.addEventListener('keydown', this.campaignKeydown, true);
        document.addEventListener('focusin', this.campaignFocusin, true);
      }

      releaseCampaignFocus() {
        if (!this.campaignBackground) return;
        document.removeEventListener('keydown', this.campaignKeydown, true);
        document.removeEventListener('focusin', this.campaignFocusin, true);
        this.campaignBackground.forEach((element) => { element.inert = false; });
        this.campaignBackground = null;
      }

      disconnectedCallback() {
        this.releaseCampaignFocus();
      }

      preprocessHTML(productElement) {
        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient')
            this.modalContent.classList.add(classApplied);
        });
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const modalDialog = productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;

        const oldId = sectionId;
        const newId = `quickadd-${sectionId}`;
        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });

        productElement.dataset.originalSection = sectionId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }
    }
  );
}
